import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink, LinkRemove } from '@factorialco/f0-react/icons/app'
import {
  JobLevelEmployeesDataCollectionDocument,
  JobLevelEmployeesDataCollectionItemFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import EmployeeSidepanel from 'modules/project_management/components/EmployeeSidepanel'
import { SelectedEmployeeInfo } from 'modules/project_management/components/tables/JobLevelEmployeesDataCollection/types'
import { getPeopleCount } from 'modules/project_management/lib/constants'
import { costRateCell } from 'modules/project_management/lib/utils'

const JobLevelEmployeesDataCollection = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { rateId } = useParams()

  const [showEmployeeSidepanel, setShowEmployeeSidepanel] = useState(false)
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState<SelectedEmployeeInfo>({
    avatar: '',
    id: '0',
    firstName: '',
    lastName: '',
    billingRateCents: 0,
    costRate: 0,
    profitabilityPercentageCents: 0,
    currency: '',
  })
  const {
    access: { locale },
  } = useCurrentSession()

  const itemOnClick = (item: JobLevelEmployeesDataCollectionItemFragment) => () => {
    track(trackingEvents.projectManagement.jobsAndRates.clickOnEditEmployeeRate, {
      origin: 'from_job_level_table',
    })
    setShowEmployeeSidepanel(true)
    const resource = item.resource
    const isEmployee = resource?.__typename === 'EmployeesEmployee'

    setSelectedEmployeeInfo({
      avatar: isEmployee ? (resource.access?.avatar ?? '') : '',
      id: isEmployee ? resource.id : '0',
      firstName: isEmployee ? (resource.firstName ?? '') : '',
      lastName: isEmployee ? (resource.lastName ?? '') : '',
      billingRateCents: item.billingRateCents,
      costRate: item.chargingCostCents,
      profitabilityPercentageCents: item.profitabilityPercentageCents,
      currency: item.currency,
    })
  }

  const source = useGraphqlDataCollectionSource({
    document: JobLevelEmployeesDataCollectionDocument,
    totalItemSummary: (totalItems) => getPeopleCount(totalItems),
    mapData: (data) => {
      return data.projectManagement.ratesConnection.nodes ?? []
    },
    mapVariables: ({ sortings, search }) => ({
      referenceRateId: rateId ?? '',
      sortOrder: sortings?.[0],
      search: search || undefined,
    }),
    mapPagination: (data) => {
      return {
        total: data.projectManagement.ratesConnection.totalCount ?? 0,
      }
    },
    sortings: {
      charging_cost_cents: {
        label: i18n.t('project_management.job_level_employees.table.headers.cost_rate.label'),
      },
      billing_rate_cents: {
        label: i18n.t('project_management.job_level_employees.table.headers.bill_rate.label'),
      },
      profitability_percentage_cents: {
        label: i18n.t('project_management.job_level_employees.table.headers.profit_margin.label'),
      },
      unlinked: {
        label: i18n.t('project_management.job_level_employees.table.headers.custom_rate.label'),
      },
    },
    search: {
      enabled: true,
    },
    itemOnClick,
    fetchPolicy: 'network-only',
  })

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('project_management.job_level_employees.table.empty_state.title'),
            description: i18n.t(
              'project_management.job_level_employees.table.empty_state.description'
            ),
            emoji: '🎯',
            actions: [
              {
                label: i18n.t(
                  'project_management.job_level_employees.table.empty_state.button_text'
                ),
                icon: ExternalLink,
                variant: 'outline',
                onClick: () => {
                  navigate(navigation.employees.list.fullPath())
                },
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.person.label'
                  ),
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    const resource = item?.resource
                    const isEmployee = resource?.__typename === 'EmployeesEmployee'

                    return {
                      type: 'person',
                      value: {
                        firstName: isEmployee ? resource.firstName : undefined,
                        lastName: isEmployee ? resource.lastName : undefined,
                        src:
                          isEmployee && resource.access?.avatar
                            ? buildUrl(resource.access.avatar)
                            : undefined,
                      },
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.cost_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.cost_rate.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'charging_cost_cents' as const,
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return costRateCell(item, undefined, locale)
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.bill_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.bill_rate.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'billing_rate_cents' as const,
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item?.billingRateCents,
                        currency: item?.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.profit_margin.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.profit_margin.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'profitability_percentage_cents' as const,
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: `${item?.profitabilityPercentageCents / 100}%`,
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.job_level_employees.table.headers.custom_rate.label'
                  ),
                  info: i18n.t(
                    'project_management.job_level_employees.table.headers.custom_rate.tooltip_message'
                  ),
                  sorting: 'unlinked' as const,
                  render: (item: JobLevelEmployeesDataCollectionItemFragment) => {
                    if (item?.unlinked) {
                      return {
                        type: 'tag',
                        value: {
                          label: i18n.t('project_management.job_level_employees.table.custom'),
                          icon: LinkRemove,
                        },
                      }
                    }
                  },
                },
              ]),
            },
          },
        ]}
      />
      {showEmployeeSidepanel && (
        <EmployeeSidepanel
          refetchQueries={[JobLevelEmployeesDataCollectionDocument]}
          showSidePanel={showEmployeeSidepanel}
          onClose={() => setShowEmployeeSidepanel(false)}
          selectedEmployeeInfo={selectedEmployeeInfo}
        />
      )}
    </>
  )
}

export default JobLevelEmployeesDataCollection
